import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, of, throwError } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { BoardState } from '../board/+store/board-state/board-state.reducer';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { loadUserBoardSuccess, clearUserBoard, updateUserBoardSuccess, deleteUserBoardSuccess } from '../board/+store/board-state/board-state.actions';
import {  selectUserBoardList } from '../board/+store/board-state/board-state.selectors';
import { AddComponentToUserBoardItem, convertUserBoardDtoToModel, convertUserBoardModelToDto, UserBoard, UserBoardComponentMode, UserBoardDto, UserBoardItem, UserBoardsDto } from './models/user-board.model';

@Injectable({
  providedIn: 'root'
})
export class UserBoardService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.userBoard;
  private userBoardApis = {
    createUserBoard: '/create-user-board',
    updateUserBoard: '/update-user-board',
    deleteUserBoard: '/delete-user-board',
    getUserBoards: '/get-user-boards',
  };

  constructor(
    private http: HttpClient,
    private boardStore: Store<BoardState>
  ) {}

  /**
   * Retrieves the user boards for the current user.
   *
   * - If the BoardState already contains user boards, returns them immediately.
   * - Otherwise, calls the backend API, dispatches an action to store the results, and returns the data.
   */
  getUserBoards(): Observable<UserBoard[]> {
    return this.boardStore.pipe(
      select(selectUserBoardList),
      take(1), // Take the current state once
      switchMap(userBoardList => {
        if (userBoardList && userBoardList.length > 0) {
          // UserBoards already exist in the store: return them as a UserBoard[].
          return of(userBoardList);
        } else {
          return this.http.post<UserBoardsDto>(
            `${this.apiUrl}${this.userBoardApis.getUserBoards}`,
            {}
          ).pipe(
            filter(response => response !== null),
            map(userBoards => {
              var boardList: UserBoard[] = [];
              userBoards.userBoards.forEach((userBoard: UserBoardDto) => {
                boardList.push(convertUserBoardDtoToModel(userBoard, UserBoardComponentMode.Live));
              });
              return boardList;
            }),
            tap(boardList => {
              // Dispatch an action to store the datasets in the state.
              this.boardStore.dispatch(loadUserBoardSuccess({ userBoards: cloneDeep(boardList) }));
            }),
            map((userBoardList: UserBoard[]) => {
              userBoardList.forEach((userBoard: UserBoard) => {
                userBoard.itemList.forEach((item: UserBoardItem) => {
                  AddComponentToUserBoardItem(item);
                })
              });
              return userBoardList;
            })
          );
        }
      }
    ));
  }

  /**
  * Creates the new UserBoard in the backend.
  *
  * - Updates BoardState upon success
  */
  createUserBoard(userBoard: UserBoard): Observable<UserBoard> {
    var postObj = convertUserBoardModelToDto(userBoard);

    return this.http.post<UserBoardDto>(
      `${this.apiUrl}${this.userBoardApis.createUserBoard}`, postObj).pipe(
      map((response: UserBoardDto) => convertUserBoardDtoToModel(response, UserBoardComponentMode.Live)),
      tap((modelResponse: UserBoard) => {
        // Dispatch an action to store the userBoard in the state.
        if(modelResponse){
          //Tap into response as this response has all the internalIds generated
          this.boardStore.dispatch(updateUserBoardSuccess({ userBoard: modelResponse }));
        }
      })
    );
  }

  /**
  * Updates the userBoard in the backend.
  *
  * - Updates BoardState upon success
  */
  updateUserBoard(userBoard: UserBoard): Observable<UserBoard> {
    var postObj = convertUserBoardModelToDto(userBoard);

    return this.http.post<UserBoardDto>(
      `${this.apiUrl}${this.userBoardApis.updateUserBoard}`, postObj).pipe(
      map((response: UserBoardDto) => convertUserBoardDtoToModel(response, UserBoardComponentMode.Live)),
      tap((modelResponse: UserBoard) => {
        // Dispatch an action to store the userBoard in the state.
        if(modelResponse){
          //Tap into response as this response has all the internalIds generated
          this.boardStore.dispatch(updateUserBoardSuccess({ userBoard: modelResponse }));
        }
      })
    );
  }



  /**
  * Deletes the new userBoard in the backend.
  *
  * - Updates BoardState upon success
  */
  deleteUserBoard(userBoard: UserBoard): Observable<boolean> {
    var postObj = {
      userBoardId: userBoard.userBoardId
    };

    return this.http.post<boolean>(
      `${this.apiUrl}${this.userBoardApis.deleteUserBoard}`, postObj).pipe(
      tap((response: boolean) => {
        // Dispatch an action to delete the board in the state.
        if(response){
          this.boardStore.dispatch(deleteUserBoardSuccess({ userBoardId: postObj.userBoardId }));
        }
      })
    );
  }

  /**
   * Clears the userBoards stored in the BoardState.
   */
  clearUserBoards(): void {
    this.boardStore.dispatch(clearUserBoard());
  }
}
